"use client";

import { mergeClasses } from "@trex-2b/libs/tw-merge";

export enum EApplicationStatus {
  PENDING = "PENDING",
  DRAFT = "DRAFT",
  PENDING_REVIEW = "PENDING_REVIEW",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export type StatusTagStatus =
  | "Approved"
  | "Pending"
  | "Pending review"
  | "Rejected"
  | "Live"
  | "Confirmed"
  | "Completed"
  | "Settling"
  | "Draft";

export type StatusTagProps = {
  status: StatusTagStatus;
  /** 可选：覆盖默认展示文案（默认展示 status） */
  text?: string;
  className?: string;
  "aria-label"?: string;
};

type StatusStyle = {
  container: string;
  dot: string;
  text: string;
};

const stylesByStatus: Record<StatusTagStatus, StatusStyle> = {
  Approved: {
    container: "h-[26px] gap-2 rounded-full bg-[#ffedda] px-2 py-1",
    dot: "bg-[#c99449]",
    text: "text-[12px] text-[#c99449]",
  },
  Completed: {
    container: "h-[26px] gap-2 rounded-full bg-[#fdfee2] px-2 py-1",
    dot: "bg-[#989800]",
    text: "text-[12px] text-[#989800]",
  },
  Confirmed: {
    container: "h-[26px] gap-2 rounded-full bg-[#fdfee2] px-2 py-1",
    dot: "bg-[#989800]",
    text: "text-[12px] text-[#989800]",
  },
  Live: {
    container:
      "h-[26px] justify-center gap-2 rounded-full bg-[#d1ffe0] px-2 py-1",
    dot: "bg-[#42925d]",
    text: "text-[12px] text-[#42925d]",
  },
  Pending: {
    container:
      "h-[26px] justify-center gap-2 rounded-full bg-[#e2f5ff] px-2 py-1",
    dot: "bg-[#59a8d4]",
    text: "text-[12px] text-[#59a8d4]",
  },
  "Pending review": {
    container:
      "h-[26px] justify-center gap-2 rounded-full bg-[#e2f5ff] px-2 py-1",
    dot: "bg-[#59a8d4]",
    text: "text-[12px] text-[#59a8d4]",
  },
  Settling: {
    container: "h-[26px] gap-2 rounded-full bg-[#ededff] px-2 py-1",
    dot: "bg-[#8a8cd9]",
    text: "text-[12px] text-[#8a8cd9]",
  },
  Draft: {
    container:
      "h-[26px] gap-2 rounded-full bg-[rgba(28,28,28,0.05)] px-2 py-px",
    // Avoid Tailwind arbitrary values containing '/' in CSS var names, which can
    // break PostCSS parsing in the generated CSS.
    dot: "bg-[rgba(28,25,22,0.6)]",
    text: "text-[12px] text-[rgba(28,25,22,0.6)]",
  },
  Rejected: {
    container:
      "h-[26px] gap-2 rounded-full bg-[rgba(28,28,28,0.05)] px-2 py-px",
    dot: "bg-[rgba(28,25,22,0.6)]",
    text: "text-[12px] text-[rgba(28,25,22,0.6)]",
  },
};

export function StatusTag({
  status,
  className,
  "aria-label": ariaLabel,
}: StatusTagProps) {
  const style = stylesByStatus[status];

  return (
    <span
      aria-label={ariaLabel ?? status}
      className={mergeClasses(
        "inline-flex items-center",
        "[font-feature-settings:'ss01'_1,'cv01'_1,'cv11'_1]",
        style.container,
        className,
      )}
    >
      <span
        aria-hidden
        className={mergeClasses("size-1 shrink-0 rounded-[2px]", style.dot)}
      />
      <span
        className={mergeClasses(
          "shrink-0 whitespace-nowrap font-normal not-italic leading-[18px] font-['Inter:Regular',sans-serif]",
          style.text,
        )}
      >
        {status}
      </span>
    </span>
  );
}
